import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Avatar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { IMsoolUnit } from '@services';
import { ChannelType } from '../../../../../services/useChannels';

// Channel logo SVGs - using existing project assets
import airbnbLogo from '@assets/svg/AirbnbLogo.svg';
import bookingLogo from '@assets/svg/BookingLogo.svg';
import gathernLogo from '@assets/svg/GathernIcon2.svg';
import msoolLogo from '@assets/svg/MsoolIcon.svg';
import { T_KEYS } from '../../../constants';

const CardContainer = styled(Box)({
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  border: '1px solid #e6e6e6',
  marginBottom: '24px',
  overflow: 'clip',
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.04)',
  padding: '1px',
});

const CardHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 24px',
  backgroundColor: '#ffffff',
});

const HeaderRight = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  justifyContent: 'flex-start',
});

const MsoolBadge = styled(Box)({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const HeaderTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#1a1a1a',
  lineHeight: '24px',
});

const TableContainer = styled(Box)({
  width: '100%',
  padding: '16px',
});

const TableContent = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: '100%',
});

const TableColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  flex: '1 0 0',
  minWidth: '0',
  minHeight: '1px',
});

const PropertyColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  width: '600px',
});

const TableHeaderContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  paddingBottom: '4px',
  width: '100%',
});

const TableHeaderTextContainer = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  padding: '8px 8px 4px 8px',
  width: '100%',
});

const TableHeaderText = styled(Typography)({
  fontSize: '12px',
  fontWeight: 700,
  color: '#37352f',
  fontFamily: '"Tajawal", sans-serif',
  textAlign: 'right',
  padding: '0 8px',
});

const Divider = styled(Box)({
  height: '1px',
  width: '100%',
  backgroundColor: '#e9e9e7',
});

const TableRowItem = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  height: '50px',
  padding: '14px 0',
});

const TableRowContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '2px 8px',
});

const PropertyRowContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  height: '50px',
  padding: '8px 0',
  width: '100%',
});

const PropertyRowContent = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  paddingRight: '16px',
});

const PropertyInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const PropertyImage = styled(Box)({
  width: '63px',
  height: '30px',
  borderRadius: '4px',
  overflow: 'hidden',
  position: 'relative',
  flexShrink: 0,
  backgroundColor: '#f5f5f5',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
});

const PropertyName = styled(Typography)({
  fontSize: '14px',
  fontWeight: 700,
  color: '#331860',
  fontFamily: '"Tajawal", sans-serif',
  textAlign: 'right',
  whiteSpace: 'nowrap',
});

const ChannelsContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const ChannelLogo = styled(Box)({
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
});


const UnmapButton = styled(Button)({
  color: '#5b3fff',
  fontSize: '12px',
  fontWeight: 400,
  textTransform: 'none',
  padding: '2px 8px',
  borderRadius: '9999px',
  border: '1px solid #5b3fff',
  backgroundColor: 'transparent',
  width: '77px',
  height: '25px',
  lineHeight: '16px',
  '&:hover': {
    backgroundColor: 'rgba(91, 63, 255, 0.04)',
    border: '1px solid #5b3fff',
  },
});

const EmptyState = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px',
  color: '#999999',
});

interface MsoolUnitsCardProps {
  units: IMsoolUnit[];
  isLoading?: boolean;
  onUnmap: (msoolUnit: IMsoolUnit) => void;
}

const renderChannelLogo = (channelType: ChannelType, index: number) => {
  const logoStyle = { borderRadius: '50%', objectFit: 'cover' as const };
  
  switch (channelType) {
    case ChannelType.Airbnb:
      return (
        <ChannelLogo key={`airbnb-${index}`}>
          <img src={airbnbLogo} alt="Airbnb" style={logoStyle} />
        </ChannelLogo>
      );
    case ChannelType.Booking:
      return (
        <ChannelLogo key={`booking-${index}`}>
          <img src={bookingLogo} alt="Booking" style={logoStyle} />
        </ChannelLogo>
      );
    case ChannelType.Gathern:
      return (
        <ChannelLogo key={`gathern-${index}`}>
          <img src={gathernLogo} alt="Gathern" style={logoStyle} />
        </ChannelLogo>
      );
    default:
      return null;
  }
};

const MsoolUnitsCard: React.FC<MsoolUnitsCardProps> = memo(({ units, isLoading, onUnmap }) => {
  const { t } = useTranslation();

  const handleUnmap = useCallback((msoolUnit: IMsoolUnit) => {
    onUnmap(msoolUnit);
  }, [onUnmap]);

  if (!units || units.length === 0) {
    return null;
  }

  return (
    <CardContainer>
      <CardHeader>
        <HeaderRight>
          <MsoolBadge>
            <img src={msoolLogo} alt="Msool" style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover' }} />
          </MsoolBadge>
          <HeaderTitle>
            {t(T_KEYS.UNITS_MAPPING_MSOOL_UNITS_TITLE)}
          </HeaderTitle>
        </HeaderRight>
        <Box sx={{ opacity: 0 }}>
          <Typography sx={{ fontSize: '14px', color: '#7a7a7a',  lineHeight: '20px' }}>
            {units.length} {t(T_KEYS.UNITS_MAPPING_UNIT)}
          </Typography>
        </Box>
      </CardHeader>

      <TableContainer>
        <TableContent>
          {/* Property Column */}
          <PropertyColumn>
            <TableHeaderContainer >
              <TableHeaderTextContainer>
                <TableHeaderText>
                  {t(T_KEYS.UNITS_MAPPING_PROPERTY)}
                </TableHeaderText>
              </TableHeaderTextContainer>
              <Divider />
            </TableHeaderContainer>
            {units.map((unit) => (
              <PropertyRowContainer key={`property-${unit.propertyId}`}>
                <PropertyRowContent>
                  <PropertyInfo>
                    <PropertyImage>
                      <img 
                        src={unit.imageUrl ?? unit.channels?.[0]?.listingImage ?? undefined} 
                        alt={unit.propertyName ?? ''}
                      />
                    </PropertyImage>
                    <PropertyName>{unit.propertyName}</PropertyName>
                  </PropertyInfo>
                </PropertyRowContent>
              </PropertyRowContainer>
            ))}
          </PropertyColumn>

          {/* Channels Column */}
          <TableColumn>
            <TableHeaderContainer>
              <TableHeaderTextContainer>
                <TableHeaderText>
                  {t(T_KEYS.UNITS_MAPPING_CHANNELS)}
                </TableHeaderText>
              </TableHeaderTextContainer>
              <Divider />
            </TableHeaderContainer>
            {units.map((unit) => (
              <TableRowItem key={`channels-${unit.propertyId}`}>
                <TableRowContent>
                  <ChannelsContainer>
                    {unit.channels?.map((linkedUnit, index) => 
                      renderChannelLogo(linkedUnit.channelType, index)
                    )}
                  </ChannelsContainer>
                </TableRowContent>
              </TableRowItem>
            ))}
          </TableColumn>

          {/* Action Column */}
          <TableColumn>
            <TableHeaderContainer>
              <TableHeaderTextContainer>
                <TableHeaderText>
                  {t(T_KEYS.UNITS_MAPPING_ACTIONS)}
                </TableHeaderText>
              </TableHeaderTextContainer>
              <Divider />
            </TableHeaderContainer>
            {units.map((unit) => (
              <TableRowItem key={`action-${unit.propertyId}`}>
                <TableRowContent>
                  <UnmapButton onClick={() => handleUnmap(unit)}>
                    {t(T_KEYS.UNITS_MAPPING_UNMAP)}
                  </UnmapButton>
                </TableRowContent>
              </TableRowItem>
            ))}
          </TableColumn>
        </TableContent>

        {units.length === 0 && (
          <EmptyState>
            <Typography>
              {t(T_KEYS.UNITS_MAPPING_NO_MAPPED)}
            </Typography>
          </EmptyState>
        )}
      </TableContainer>
    </CardContainer>
  );
});

MsoolUnitsCard.displayName = 'MsoolUnitsCard';

export default MsoolUnitsCard;
